/**
 * VoiceInputButton — Stage 10 Voice Input.
 *
 * Microphone button rendered next to the chat composer. Tapping starts speech
 * recognition; tapping again stops and finalises. The current `interim`
 * transcript is fed back to the parent via `onInterim` so the chat input
 * shows live captions while the user is speaking.
 *
 * Visual states:
 *   • idle       — calm circular button with mic icon
 *   • listening  — accent pulse ring + mini waveform animation, mic switches
 *                  to a stop icon
 *   • unsupported — button hidden entirely (parent decides whether to render
 *                   a caption noting "voice not supported")
 *
 * Mobile-first: 44×44 minimum touch target, no hover-only affordances.
 */
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Microphone, Stop } from '@phosphor-icons/react';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { toast } from 'sonner';

export const VoiceInputButton = ({
  onResult,            // (final, finalThenInterim) => void
  onInterim,           // (interimText) => void
  onListeningChange,   // (bool) => void
  size = 36,
}) => {
  const { supported, listening, transcript, interim, error, start, stop, reset } = useSpeechToText();

  useEffect(() => {
    if (error) toast.error(error === 'not-allowed' ? 'Microphone permission was blocked. Allow it in your browser to use voice.' : 'Voice input had a hiccup. Try again?');
  }, [error]);

  // Live interim captions stream up to the parent so the input field can
  // show the user's words in real-time.
  useEffect(() => {
    if (!listening) return;
    const live = (transcript ? transcript + ' ' : '') + (interim || '');
    onInterim && onInterim(live.trim());
  }, [listening, transcript, interim, onInterim]);

  // Notify parent when listening state flips so it can suppress the typed
  // input field's own placeholder, etc.
  useEffect(() => { onListeningChange && onListeningChange(listening); }, [listening, onListeningChange]);

  // On stop we surface the final transcript via `onResult` and reset for
  // the next session.
  const handleStop = () => {
    stop();
    // Allow the recognition engine to flush its final result.
    setTimeout(() => {
      const final = transcript.trim();
      if (final) onResult && onResult(final);
      reset();
    }, 250);
  };

  if (!supported) return null;

  const handleTap = () => (listening ? handleStop() : (reset(), start()));

  return (
    <button
      type="button"
      onClick={handleTap}
      className={[
        'relative flex items-center justify-center rounded-full transition-colors flex-shrink-0',
        'min-w-[36px] min-h-[36px]',
        listening
          ? 'bg-red-500 text-white hover:bg-red-600'
          : 'text-slate-400 hover:text-[#0E1C2E] hover:bg-slate-50',
      ].join(' ')}
      style={{ width: size, height: size }}
      data-testid="chat-voice-input-button"
      aria-label={listening ? 'Stop voice input' : 'Start voice input'}
      title={listening ? 'Tap to send' : 'Speak to Kindra'}
    >
      <AnimatePresence mode="wait" initial={false}>
        {listening ? (
          <motion.div
            key="listening"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="flex items-center justify-center"
          >
            <Stop size={14} weight="fill" />
          </motion.div>
        ) : (
          <motion.div
            key="idle"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="flex items-center justify-center"
          >
            <Microphone size={16} weight="duotone" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pulse ring while listening */}
      {listening && (
        <>
          <span className="absolute inset-0 rounded-full bg-red-400/30 animate-ping" aria-hidden />
          {/* Mini waveform — 3 bars that scale with audio activity heuristic */}
          <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-end gap-[2px]" aria-hidden>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="block w-[2px] rounded-full bg-red-400"
                style={{
                  height: 6 + (i % 2 === 0 ? 4 : 2),
                  animation: `kindraVoiceBar 0.7s ease-in-out ${i * 0.12}s infinite`,
                }}
              />
            ))}
          </span>
        </>
      )}
    </button>
  );
};

export default VoiceInputButton;
